import React, {useCallback, useState} from 'react';
import _ from 'underscore';

import CalendarActions from 'calendar/CalendarActions';
import {CalendarFile} from 'calendar/CalendarConstants';
import CalendarStore from 'calendar/CalendarStore';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileBrowserUtil, {FileIconNameItem} from 'file-browser/FileBrowserUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import fuzzydate from 'util/fuzzydate';

import CardSection, {
  CardSectionLoadingRows,
  CardSectionMessageRow,
  CardSectionRowList,
  CardSectionRowProps,
} from './CardSection';
import {
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  LOADING_SKELETON_ROW_COUNT,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.RECENTS;

const MAX_DAYS_BACK = 7;

/** Lists recently viewed notebook files from the calendar store for quick reopen. */
export default function RecentsSection(): JSX.Element {
  const todayDate = useStoreSelector(
    CalendarStore,
    useCallback((store) => store.getToday(), [])
  );
  const files = useStoreSelector(
    CalendarStore,
    useCallback((store) => store.getFiles(), [])
  );

  const [showMore, setShowMore] = useState(false);

  useOnMount(() => {
    _.defer(() => {
      const filesStartDate = todayDate.clone().subtract(MAX_DAYS_BACK, 'days');
      const filesEndDate = todayDate.clone().add(2, 'days');
      CalendarActions.fetchFiles(filesStartDate, filesEndDate);
    });
  });

  let recentFiles: Array<CalendarFile> = [];

  for (let i = 0; i < MAX_DAYS_BACK; i++) {
    const lastDate = todayDate.clone().subtract(i, 'days').format('YYYY-MM-DD');
    const filesForDate = files[lastDate];
    if (filesForDate == null) {
      break;
    }
    recentFiles = _.unique([...recentFiles, ...filesForDate], 'id');
  }

  const pickUpSectionRows: Array<CardSectionRowProps> = _.unique(recentFiles, 'id')
    .sort((a, b) => b.endAccess.localeCompare(a.endAccess))
    .slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY)
    .map((file) => ({
      id: file.id,
      icon: FileBrowserUtil.getFileIconName(file as FileIconNameItem),
      name: file.name,
      onClick: () => {
        FileBrowserActions.openFile(file.id);
        SidepanelActions.close({autoOpenFile: false});
      },
      rightContent: file.endAccess
        ? `Viewed ${fuzzydate(file.endAccess, {showYear: false, showWeekday: true})}`
        : null,
    }));

  return (
    <CardSection title="Jump back in" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      {_.isEmpty(files) && <CardSectionLoadingRows count={LOADING_SKELETON_ROW_COUNT} />}
      {!_.isEmpty(files) && pickUpSectionRows.length === 0 && (
        <CardSectionMessageRow
          message="No recent files"
          dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
        />
      )}
      {!_.isEmpty(files) && pickUpSectionRows.length > 0 && <CardSectionRowList rows={pickUpSectionRows} />}
      {recentFiles.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
        />
      )}
    </CardSection>
  );
}
